import {Loop} from '@mazechazer/redux-loop'
import {Decoder} from 'json-bouncer'
import {initLoginForm} from '../Components/LoginForm/LoginFormReducer'
import {FeedbackLoop} from '../Data/FeedbackLoop'
import {decodeJson} from '../Data/DecodeHelper'
import {
    decodeHttpResponse,
    HttpError,
    sendRequest,
    tokenExpired
} from '../Data/Http'
import * as HttpRequest from '../Data/HttpRequest'
import * as Location from '../Data/Location'
import * as Nullable from '../Data/Nullable'
import {update} from '../Data/ObjectHelper'
import * as Result from '../Data/Result'
import {Session, sessionDecoder} from '../Data/Session'
import {initChooseManufacturerPage} from '../Pages/ChooseManufacturer/ChooseManufacturerReducer'
import * as InquiryDetailsReducer
    from '../Pages/InquiryDetails/InquiryDetailsReducer'
import * as InquiryOverviewReducer
    from '../Pages/InquiryOverview/InquiryOverviewReducer'
import * as OrderCreateReducer from '../Pages/OrderCreate/OrderCreateReducer'
import * as OrderDetailsReducer from '../Pages/OrderDetails/OrderDetailsReducer'
import * as OrderExportCreateReducer
    from '../Pages/OrderExportCreate/OrderExportCreateReducer'
import * as OrderExportOverviewReducer
    from '../Pages/OrderExportOverview/OrderExportOverviewReducer'
import * as OrderOverviewReducer
    from '../Pages/OrderOverview/OrderOverviewReducer'
import * as PasswordChangeReducer
    from '../Pages/PasswordChange/PasswordChangeReducer'
import * as PrismaRequestDetailsReducer
    from '../Pages/PrismaRequestDetails/PrismaRequestDetailsReducer'
import * as PrismaRequestOverviewReducer
    from '../Pages/PrismaRequestOverview/PrismaRequestOverviewReducer'
import * as ProductCalculationDetailsReducer
    from '../Pages/ProductCalculationDetails/ProductCalculationDetailsReducer'
import * as ProductCalculationImportReducer
    from '../Pages/ProductCalculationImport/ProductCalculationImportReducer'
import * as ProductCalculationOverviewReducer
    from '../Pages/ProductCalculationOverview/ProductCalculationOverviewReducer'
import {Action} from './Actions'
import {
    logout,
    reduceInquiryDetailsFeedback,
    reduceOrderDetailsFeedback
} from './FeedbackProcessing'
import {Relogin, TaggedDownloadRequest, TaggedRequest} from './HttpManagement'
import {PageLoadIdentifier} from './PageLoadIdentifier'
import {
    reduceChooseManufacturerAction,
    reduceInquiryDetailsAction,
    reduceInquiryOverviewAction,
    reduceLoginAction,
    reduceOrderCreateAction,
    reduceOrderDetailsAction,
    reduceOrderExportCreateAction,
    reduceOrderExportOverviewAction,
    reduceOrderOverviewAction,
    reducePasswordChangeAction,
    reducePasswordLostAction,
    reducePrismaRequestDetailsAction,
    reducePrismaRequestOverviewAction,
    reduceProductCalculationDetailsAction,
    reduceProductCalculationImportAction,
    reduceProductCalculationOverviewAction,
    reduceReloginAction
} from './ReducerProcessing'
import * as Routing from './Routing'
import {dispatchNavigation, initializePublicPage} from './Routing'
import {Authenticated, AuthenticatedPage, State} from './State'

export function init(
    storedSession: string | null,
    hash: string,
): Loop<State, Action> {
    const session: Session | null =
        Nullable.andThen(
            sessionJson =>
                Result.toNullable(
                    decodeJson(sessionDecoder(), sessionJson),
                ),
            storedSession,
        )
    return Routing.run(session, hash)
}

export function reducer(state: State, action: Action): Loop<State, Action> {
    switch (action.type) {
        case 'NavigationAction': {
            return ((): Loop<State, Action> => {
                const location = Location.fromHash(action.hash)
                switch (state.type) {
                    case 'Public': {
                        const pageLoop = initializePublicPage(location)
                        return {
                            state:
                                update(
                                    {
                                        type: 'Public',
                                        page: pageLoop.state,
                                    },
                                    state,
                                ),
                            effects: pageLoop.effects,
                        }
                    }
                    case 'ChoosingManufacturer':
                        return {
                            state:
                                update(
                                    {
                                        type: 'ChoosingManufacturer',
                                        page: {
                                            type: 'ChooseManufacturerPage',
                                            state: initChooseManufacturerPage(),
                                        },
                                        intermediateSession: state.intermediateSession,
                                    },
                                    state,
                                ),
                            effects: [],
                        }
                    case 'Authenticated': {
                        return dispatchNavigation(
                            update({drawerOpen: false}, state),
                            location,
                        )
                    }
                }
            })()
        }
        case 'DrawerToggleAction':
            return {
                state: update({
                    drawerOpen: action.open,
                }, state),
                effects: [],
            }
        case 'LogoutAction':
            return logout({state, effects: []})
        case 'HttpResponse':
            return ((): Loop<State, Action> => {
                const {httpRequest, receiver, result} = action
                if (state.type === 'Authenticated' && tokenExpired(result)) {
                    const
                        failedRequest: TaggedRequest = {httpRequest, receiver},
                        newRelogin: Relogin =
                            state.relogin === null ?
                                {
                                    failedPageLoadRequest: null,
                                    failedRequests: [failedRequest],
                                    failedDownloadRequests: [],
                                    modal:
                                        initLoginForm(
                                            state.session.cis2User.email),
                                } :
                                update({
                                    failedRequests:
                                        state.relogin.failedRequests
                                            .concat(failedRequest)
                                }, state.relogin)
                    return {
                        state: update({relogin: newRelogin}, state),
                        effects: [],
                    }
                } else {
                    return ((): Loop<State, Action> => {
                        switch (action.receiver.type) {
                            case 'OrderOverviewHttpResponse':
                                return reduceOrderOverviewAction(
                                    state,
                                    OrderOverviewReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'OrderDetailsHttpResponse':
                                return reduceOrderDetailsAction(
                                    state,
                                    OrderDetailsReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'OrderExportCreateHttpResponse':
                                return reduceOrderExportCreateAction(
                                    state,
                                    OrderExportCreateReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'PasswordChangeResponse':
                                return reducePasswordChangeAction(
                                    state,
                                    PasswordChangeReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'CreateOrderHttpResponse':
                                return reduceOrderCreateAction(
                                    state,
                                    OrderCreateReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'InquiryOverviewHttpResponse':
                                return reduceInquiryOverviewAction(
                                    state,
                                    InquiryOverviewReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    )
                                )
                            case 'InquiryDetailsHttpResponse':
                                return reduceInquiryDetailsAction(
                                    state,
                                    InquiryDetailsReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'ProductCalculationOverviewHttpResponse':
                                return reduceProductCalculationOverviewAction(
                                    state,
                                    ProductCalculationOverviewReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'ProductCalculationDetailsHttpResponse':
                                return reduceProductCalculationDetailsAction(
                                    state,
                                    ProductCalculationDetailsReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'ProductCalculationImportHttpResponse':
                                return reduceProductCalculationImportAction(
                                    state,
                                    ProductCalculationImportReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'PrismaRequestDetailsHttpResponse':
                                return reducePrismaRequestDetailsAction(
                                    state,
                                    PrismaRequestDetailsReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'PrismaRequestOverviewHttpResponse':
                                return reducePrismaRequestOverviewAction(
                                    state,
                                    PrismaRequestOverviewReducer.httpResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                        }
                    })()
                }
            })()
        case 'HttpDownloadResponse': {
            return ((): Loop<State, Action> => {
                const {httpRequest, filename, receiver, result} = action
                if (state.type === 'Authenticated' && tokenExpired(result)) {
                    const
                        failedRequest: TaggedDownloadRequest = {
                            httpRequest,
                            filename,
                            receiver
                        },
                        newRelogin: Relogin =
                            state.relogin === null ?
                                {
                                    failedPageLoadRequest: null,
                                    failedRequests: [],
                                    failedDownloadRequests: [failedRequest],
                                    modal:
                                        initLoginForm(
                                            state.session.cis2User.email),
                                } :
                                update({
                                    failedDownloadRequests:
                                        state.relogin.failedDownloadRequests
                                            .concat(failedRequest)
                                }, state.relogin)
                    return {
                        state: update({relogin: newRelogin}, state),
                        effects: [],
                    }
                } else {
                    return (() => {
                        switch (action.receiver.type) {
                            case 'OrderDetailsHttpDownloadResponse':
                                return reduceOrderDetailsAction(
                                    state,
                                    OrderDetailsReducer.httpDownloadResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'InquiryDetailsHttpDownloadResponse':
                                return reduceInquiryDetailsAction(
                                    state,
                                    InquiryDetailsReducer.httpDownloadResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'OrderExportCreateHttpDownloadResponse':
                                return reduceOrderExportCreateAction(
                                    state,
                                    OrderExportCreateReducer.httpDownloadResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    ),
                                )
                            case 'OrderExportOverviewHttpDownloadResponse':
                                return reduceOrderExportOverviewAction(
                                    state,
                                    OrderExportOverviewReducer.httpDownloadResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    )
                                )
                            case 'PrismaRequestDetailsHttpDownloadResponse':
                                return reducePrismaRequestDetailsAction(
                                    state,
                                    PrismaRequestDetailsReducer.httpDownloadResponseAction(
                                        action.receiver.identifier,
                                        action.result,
                                    )
                                )
                        }
                    })()
                }
            })()
        }
        case 'PageLoadResponse':
            if (state.type === 'Authenticated') {
                const loop =
                    ((): Loop<State, Action> => {
                        if (tokenExpired(action.result)) {
                            const
                                failedPageLoadRequest = {
                                    identifier: action.identifier,
                                    httpRequest: action.httpRequest,
                                },
                                newRelogin: Relogin =
                                    state.relogin === null ?
                                        {
                                            failedPageLoadRequest: failedPageLoadRequest,
                                            failedRequests: [],
                                            failedDownloadRequests: [],
                                            modal:
                                                initLoginForm(
                                                    state.session.cis2User.email),
                                        } :
                                        update({failedPageLoadRequest}, state.relogin)
                            return {
                                state: update({relogin: newRelogin}, state),
                                effects: [],
                            }
                        } else {
                            return ((): Loop<State, Action> => {
                                const
                                    {session} = state,
                                    {identifier} = action
                                switch (identifier.type) {
                                    case 'OrderOverview':
                                        return processPageLoadResult(
                                            OrderOverviewReducer.initialDecoder(),
                                            (value, previousState) => {
                                                const previousPage =
                                                    previousState.page
                                                if (previousPage.type === 'OrderOverviewPage') {
                                                    return {
                                                        type: 'OrderOverviewPage',
                                                        state:
                                                            OrderOverviewReducer.refresh(
                                                                value,
                                                                identifier.pageSection,
                                                                previousPage.state,
                                                            ),
                                                    }
                                                } else {
                                                    return {
                                                        type: 'OrderOverviewPage',
                                                        state:
                                                            OrderOverviewReducer.init(
                                                                value,
                                                                identifier.pageSection,
                                                            ),
                                                    }
                                                }
                                            },
                                            action,
                                            state,
                                        )
                                    case 'OrderDetails':
                                        return processPageLoadResultWithFeedback(
                                            OrderDetailsReducer.initialDecoder(),
                                            value => {
                                                const orderDetailsLoop =
                                                    OrderDetailsReducer.init(
                                                        session,
                                                        value,
                                                    )
                                                return {
                                                    state: {
                                                        type: 'OrderDetailsPage',
                                                        state: orderDetailsLoop.state,
                                                    },
                                                    feedback: orderDetailsLoop.feedback,
                                                }
                                            },
                                            reduceOrderDetailsFeedback,
                                            action,
                                            state,
                                        )
                                    case 'InquiryOverview':
                                        return processPageLoadResult(
                                            InquiryOverviewReducer.initialDecoder(),
                                            value => ({
                                                type: 'InquiryOverviewPage',
                                                state:
                                                    InquiryOverviewReducer.init(
                                                        value,
                                                        identifier.pageSection
                                                    ),
                                            }),
                                            action,
                                            state,
                                        )
                                    case 'InquiryDetails':
                                        return processPageLoadResultWithFeedback(
                                            InquiryDetailsReducer.initialDecoder(),
                                            value => {
                                                const inquiryDetailsLoop =
                                                    InquiryDetailsReducer.init(value)
                                                return {
                                                    state: {
                                                        type: 'InquiryDetailsPage',
                                                        state: inquiryDetailsLoop.state,
                                                    },
                                                    feedback: inquiryDetailsLoop.feedback,
                                                }
                                            },
                                            reduceInquiryDetailsFeedback,
                                            action,
                                            state,
                                        )
                                    case 'OrderExportCreate':
                                        return processPageLoadResult(
                                            OrderExportCreateReducer.initialDecoder(),
                                            value => ({
                                                type: 'OrderExportCreatePage',
                                                state:
                                                    OrderExportCreateReducer
                                                        .initWithTemplate(value),
                                            }),
                                            action,
                                            state,
                                        )
                                    case 'OrderExportOverview':
                                        return processPageLoadResult(
                                            OrderExportOverviewReducer.initialDecoder(),
                                            value => ({
                                                type: 'OrderExportOverviewPage',
                                                state:
                                                    OrderExportOverviewReducer
                                                        .init(value),
                                            }),
                                            action,
                                            state,
                                        )
                                    case 'ProductCalculationOverview':
                                        return processPageLoadResult(
                                            ProductCalculationOverviewReducer.initialDecoder(),
                                            value => ({
                                                type: 'ProductCalculationOverviewPage',
                                                state:
                                                    ProductCalculationOverviewReducer
                                                        .init(value)
                                            }),
                                            action,
                                            state,
                                        )
                                    case 'ProductCalculationDetails':
                                        return processPageLoadResult(
                                            ProductCalculationDetailsReducer.initialDecoder(),
                                            value => ({
                                                type: 'ProductCalculationDetailsPage',
                                                state:
                                                    ProductCalculationDetailsReducer
                                                        .init(value),
                                            }),
                                            action,
                                            state,
                                        )
                                    case 'ProductCalculationImport':
                                        return processPageLoadResult(
                                            ProductCalculationImportReducer.initialDecoder(),
                                            value => ({
                                                type: 'ProductCalculationImportPage',
                                                state:
                                                    ProductCalculationImportReducer
                                                        .init(value),
                                            }),
                                            action,
                                            state,
                                        )
                                    case 'PrismaRequestOverview':
                                        return processPageLoadResult(
                                            PrismaRequestOverviewReducer.initialDecoder(),
                                            value => ({
                                                type: 'PrismaRequestOverviewPage',
                                                state:
                                                    PrismaRequestOverviewReducer
                                                        .init(value),
                                            }),
                                            action,
                                            state,
                                        )
                                    case 'PrismaRequestDetails':
                                        return processPageLoadResult(
                                            PrismaRequestDetailsReducer.initialDecoder(),
                                            value => ({
                                                type: 'PrismaRequestDetailsPage',
                                                state:
                                                    PrismaRequestDetailsReducer.init(value),
                                            }),
                                            action,
                                            state,
                                        )
                                }
                            })()
                        }
                    })()
                const newRunningPageLoads = state.runningPageLoads - 1
                return {
                    state: update(
                        {
                            runningPageLoads: newRunningPageLoads,
                            loadingIndicator: state.loadingIndicator && newRunningPageLoads > 0,
                        },
                        loop.state,
                    ),
                    effects: loop.effects,
                }
            } else {
                return {state, effects: []}
            }
        case 'PageLoadSoftTimeout':
            if (state.type === 'Authenticated' && state.runningPageLoads > 0) {
                return {
                    state: update({loadingIndicator: true}, state),
                    effects: [],
                }
            } else {
                return {state, effects: []}
            }
        case 'RetryPageLoad':
            if (state.type === 'Authenticated' && state.pageLoad.type === 'Failure') {
                const {identifier, httpRequest} = state.pageLoad
                return {
                    state: update({pageLoad: {type: 'Loading'}}, state),
                    effects: [
                        sendRequest(
                            (result): Action => ({
                                type: 'PageLoadResponse',
                                httpRequest,
                                identifier,
                                result,
                            }),
                            HttpRequest.authorize(
                                state.session.authentication.token,
                                httpRequest
                            ),
                        )
                    ]
                }
            } else {
                return {state, effects: []}
            }
        case 'PasswordLostPageAction':
            return reducePasswordLostAction(state, action.subAction)
        case 'PasswordChangePageAction':
            return reducePasswordChangeAction(state, action.subAction)
        case 'LoginPageAction':
            return reduceLoginAction(state, action.subAction)
        case 'ChooseManufacturerPageAction':
            return reduceChooseManufacturerAction(state, action.subAction)
        case 'OrderDetailsPageAction':
            return reduceOrderDetailsAction(state, action.subAction)
        case 'OrderOverviewPageAction':
            return reduceOrderOverviewAction(state, action.subAction)
        case 'OrderCreatePageAction':
            return reduceOrderCreateAction(state, action.subAction)
        case 'OrderExportCreatePageAction':
            return reduceOrderExportCreateAction(state, action.subAction)
        case 'OrderExportOverviewPageAction':
            return reduceOrderExportOverviewAction(state, action.subAction)
        case 'InquiryOverviewPageAction':
            return reduceInquiryOverviewAction(state, action.subAction)
        case 'InquiryDetailsPageAction':
            return reduceInquiryDetailsAction(state, action.subAction)
        case 'ProductCalculationOverviewPageAction':
            return reduceProductCalculationOverviewAction(state, action.subAction)
        case 'ProductCalculationDetailsPageAction':
            return reduceProductCalculationDetailsAction(state, action.subAction)
        case 'ProductCalculationImportPageAction':
            return reduceProductCalculationImportAction(state, action.subAction)
        case 'PrismaRequestOverviewPageAction':
            return reducePrismaRequestOverviewAction(state, action.subAction)
        case 'PrismaRequestDetailsPageAction':
            return reducePrismaRequestDetailsAction(state, action.subAction)
        case 'ReloginModalAction':
            return reduceReloginAction(state, action.subAction)
        case 'NoOp':
        default:
            return {state, effects: []}
    }
}

function processPageLoadResult<T>(
    decoder: Decoder<T>,
    initialize: (value: T, previousState: Authenticated) => AuthenticatedPage,
    {result, identifier, httpRequest}: {
        result: Result.Result<HttpError, string>,
        identifier: PageLoadIdentifier,
        httpRequest: HttpRequest.HttpRequest,
    },
    state: Authenticated,
): Loop<State, Action> {
    const decodedResult = decodeHttpResponse(decoder, result)
    switch (decodedResult.type) {
        case 'Ok':
            const page = initialize(decodedResult.value, state)
            return {
                state: update(
                    {
                        page,
                        pageLoad: {type: 'Default'},
                    },
                    state,
                ),
                effects: [],
            }
        case 'Err':
            return {
                state: update(
                    {
                        pageLoad: {
                            type: 'Failure',
                            error: decodedResult.error,
                            identifier,
                            httpRequest,
                        },
                    },
                    state
                ),
                effects: [],
            }
    }
}

function processPageLoadResultWithFeedback<T, F>(
    decoder: Decoder<T>,
    initialize: (value: T) => FeedbackLoop<AuthenticatedPage, F>,
    reduceFeedback: (
        feedback: F[],
        loop: Loop<State, Action>
    ) => Loop<State, Action>,
    {result, identifier, httpRequest}: {
        result: Result.Result<HttpError, string>,
        identifier: PageLoadIdentifier,
        httpRequest: HttpRequest.HttpRequest,
    },
    state: Authenticated,
): Loop<State, Action> {
    const decodedResult = decodeHttpResponse(decoder, result)
    switch (decodedResult.type) {
        case 'Ok':
            const feedbackLoop = initialize(decodedResult.value)
            const page = feedbackLoop.state
            return reduceFeedback(
                feedbackLoop.feedback,
                {
                    state: update(
                        {
                            page,
                            pageLoad: {type: 'Default'},
                        },
                        state,
                    ),
                    effects: [],
                }
            )
        case 'Err':
            return {
                state: update(
                    {
                        pageLoad: {
                            type: 'Failure',
                            error: decodedResult.error,
                            identifier,
                            httpRequest,
                        },
                    },
                    state
                ),
                effects: [],
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/App/Reducer.ts